import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import EmptyState from '@polestar/component-warehouse-react/components/empty-state/EmptyState';
import { DarkThemeProvider, LightThemeProvider, } from '@polestar/component-warehouse-react/components/theme-providers/ThemeProviders';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import { createCustomSpacing } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import FramedSection from '@polestar/component-warehouse-react/components/framed-section/FramedSection';
import { animate, m as motion, useMotionValue, useTransform, } from 'framer-motion';
import { createCSSTransition, Transitions, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Colors, { blend } from '@polestar/component-warehouse-react/tokens/colors/Colors';
import Opacities from '@polestar/component-warehouse-react/tokens/opacities/Opacities';
import { keyframes } from '@emotion/react';
import Column from '@polestar/component-warehouse-react/components/column/Column';
import { MainAxisAlignments } from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import { useHeader } from '@polestar/component-warehouse-react/components/header/HeaderContext';
/**
 * Variants of the empty state section
 * Either with galaxy background image or minimal with light grey background color
 */
export var EmptyStateSectionVariants;
(function (EmptyStateSectionVariants) {
    EmptyStateSectionVariants["withBackgroundImage"] = "withBackgroundImage";
    EmptyStateSectionVariants["minimal"] = "minimal";
})(EmptyStateSectionVariants || (EmptyStateSectionVariants = {}));
const fontBase = 'https://www.polestar.com/shared-assets/404/';
const images = {
    [MediaQueries.desktopWide]: ['space.v2@1x', 'space.v2@2x'],
    [MediaQueries.desktop]: ['space.v2@1248w', 'space.v2@1248w2x'],
    [MediaQueries.tablet]: ['space.v2@960w', 'space.v2@960w2x'],
    [MediaQueries.mobile]: ['space-mobile.v2@768w', 'space-mobile.v2@768w2x'],
};
const createUrl = (media, size = '1x') => `${fontBase}${images[media][size === '2x' ? 1 : 0]}`;
const EmptyStateSection = forwardRef(({ variant = EmptyStateSectionVariants.withBackgroundImage, skipTopMargin, isFirstSection, ...props }, ref) => {
    const imageRef = useRef(null);
    const backgroundAnimationProgress = useMotionValue(0);
    const handleLoad = useCallback(() => {
        animate(backgroundAnimationProgress, 1, {
            ...Transitions.largeSurface,
            duration: 4,
        });
    }, [backgroundAnimationProgress]);
    useEffect(() => {
        if ((imageRef === null || imageRef === void 0 ? void 0 : imageRef.current) && imageRef.current.complete) {
            handleLoad();
        }
    }, [handleLoad]);
    const { safeAreaHeight } = useHeader();
    const opacity = useTransform(backgroundAnimationProgress, [0, 1], [0, 1]);
    const scale = useTransform(backgroundAnimationProgress, [0, 1], [1.1, 1]);
    const Provider = variant === EmptyStateSectionVariants.minimal ? LightThemeProvider : DarkThemeProvider;
    // Center content below header
    const contentProps = !!isFirstSection && !skipTopMargin
        ? { style: { marginBlockStart: safeAreaHeight, marginBlockEnd: 0 } }
        : {};
    return (React.createElement(Provider, null,
        React.createElement(FramedSection, { ref: ref, skipTopMargin: skipTopMargin, backdrop: variant === EmptyStateSectionVariants.withBackgroundImage && (React.createElement(StyledPicture, null,
                Object.keys(images).map((media) => (React.createElement(React.Fragment, { key: media },
                    React.createElement("source", { srcSet: `${createUrl(media)}.jpg 1x, ${createUrl(media, '2x')}.jpg 2x`, type: "image/jpeg", media: media.replace('@media', '') }),
                    React.createElement("source", { srcSet: `${createUrl(media)}.webp 1x, ${createUrl(media, '2x')}.webp 2x`, type: "image/webp", media: media.replace('@media', '') })))),
                React.createElement(StyledImg, { ref: imageRef, src: `${createUrl(MediaQueries.tablet)}.jpg`, onLoad: handleLoad, style: {
                        opacity,
                        scale,
                    }, alt: "", role: "presentation" }))), contentProps: contentProps, backgroundColor: variant === EmptyStateSectionVariants.minimal
                ? blend(Colors.signalWhite, Colors.white, Opacities.medium)
                : undefined, customHeight: "80vh", staticViewportHeight: false },
            React.createElement(Column, { mainAxisAlignment: MainAxisAlignments.center },
                React.createElement(StyledEmptyState, { ...props })))));
});
export default EmptyStateSection;
const fadeInDelayed = keyframes `
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;
const StyledEmptyState = styled(EmptyState) `
  max-width: ${createCustomSpacing(24)};
  animation: ${createCSSTransition(fadeInDelayed.name, ' ease-in-out', 2000)};

  ${fadeInDelayed};
`;
const fadeIn = keyframes `
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;
const StyledPicture = styled.picture `
  background: ${Colors.black};
  animation: ${createCSSTransition(fadeIn.name, ' ease-in-out', 1000)};

  ${fadeIn};
`;
const StyledImg = styled(motion.img) `
  height: 100%;
  width: 100%;
  object-fit: cover;
`;
